goog.provide('el8.shared.util.cqm');

goog.require('el8.service.tz');

// Utility functions for interpreting and presenting the results of CQM calculations

el8.shared.util.cqm = (function (){
    var that = {};

    that.findRecordIndex = function(records, queryDate) {
        // From a list of timelapse records, find the one that is applicable for queryDate.
        // Two distinct interpretations of 'applicable' are:
        //
        //   1. We want the (unique) record relevant to the measurement period starting on
        //      queryDate.
        //
        //   2. We want the record relevant to some measurement period that contains queryDate.
        //      For example, record A might cover a 90-day period starting 1/1/2017 while
        //      record B covers a 90-day period starting 1/5/2017. Both records are applicable
        //      to a query date of 1/10/2017, and the behavior of this function is undefined.
        //
        // For usage 1, this function supports all measurement period types. For usage 2, this
        // function supports only measurement period types where the end date of each record is
        // exactly the 'since' date of the next record, e.g. CALENDAR_YEAR and QUARTERLY.
        return _.sortedIndex(records, queryDate, function (record) {
            // Annoyingly, this iteratee also gets applied to the search value queryDate
            if (record === queryDate) { return queryDate; }
            // We are looking for the last record with date less than or equal to queryDate.
            // The sortedIndex binary search returns the first record greater than or equal
            // to the query. The -1 millisecond offset here and -1 index offset below get us
            // to what we want.
            return record['since'] === null ? -Infinity : Date.parse(record['since']) - 1;
        }) - 1;
    };

    that.findRecord = function(records, queryDate) {
        return records[that.findRecordIndex(records, queryDate)];
    };

    that.findRecordAndDeadline = function(records, queryDate, noDeadlineLookahead) {
        var i = that.findRecordIndex(records, queryDate);
        var record = records[i];
        var deadline = null;
        while (i < records.length) {
            if (records[i]['result'] === null) {
                if (!records[i]['deadline']) {
                    // If a deadline was not calculated when a result was null, this means
                    // deadlines are not being calculated at all for this label's records
                    // and we can stop looking.
                    break;
                }

                var d = records[i]['deadline'];
                deadline = {
                    min_start: d['min_start'] ? Date.parse(d['min_start']) : null,
                    max_start: d['max_start'] ? Date.parse(d['max_start']) : null,
                    min_end: d['min_end'] ? Date.parse(d['min_end']) : null,
                    max_end: d['max_end'] ? Date.parse(d['max_end']) : null
                };

                if (noDeadlineLookahead ||
                    (deadline.max_start !== null && deadline.max_start >= queryDate) ||
                    (deadline.max_start === null && deadline.max_end !== null && deadline.max_end > queryDate) ||
                    (deadline.max_start === null && deadline.max_end === null)) {
                    // noDeadlineLookahead means we want the deadline on the record itself;
                    // otherwise we're looking for the first deadline that's not expired yet
                    break;
                }
            }
            i++;
        }
        return [record, deadline];
    };

    that.findRecordsAndDeadlines = function(timelapseMap, labels, queryDate, noDeadlineLookahead) {
        var records = {};
        _.each(labels, function(label) {
            if (label.startswith('~')) { label = label.substr(1); }
            records[label] = that.findRecordAndDeadline(timelapseMap[label], queryDate,
                                                        noDeadlineLookahead);
        });
        return records;
    };

    that.findReminderState = function(timelapseMap, queryDate) {
        return {
            relevant: that.findRecord(timelapseMap['$relevant'], queryDate).result,
            excluded: that.findRecord(timelapseMap['$excluded'], queryDate).result,
            pending: that.findRecord(timelapseMap['$pending'], queryDate).result
        };
    };

    that.analyzeDeadline = function(deadline) {
        var dueStartDate = null;
        var dueEndDate = null;
        if (deadline) {
            var min_start = deadline['min_start'];
            var max_start = deadline['max_start'];
            var min_end = deadline['min_end'];
            var max_end = deadline['max_end'];

            if ((min_start !== null && max_start !== null && min_start > max_start) ||
                (min_end !== null && max_end !== null && min_end > max_end)) {
                // This deadline is impossible to satisfy
                dueStartDate = 1;
                dueEndDate = 0;
            }
            else {
                // Due end is the last date when the item can be started, i.e. acted upon
                dueEndDate = max_start !== null ? max_start : max_end;
                dueStartDate = min_start !== null ? min_start : min_end;
            }
        }

        return [dueStartDate, dueEndDate];
    };

    that.calculateDueDates = function(deadline) {
        var deadlineDates = that.analyzeDeadline(deadline);
        var dueStartDate = deadlineDates[0];
        var dueEndDate = deadlineDates[1];

        if (dueEndDate !== null && dueStartDate !== null && dueEndDate < dueStartDate) {
            // This deadline is impossible to satisfy
            return null;
        }

        // Offset to ensure that due start/end dates are inclusive of that full day in the practice's timezone
        // (assuming measures are calculated in the server's timezone).
        // Also gives a more user-friendly interpretation of end date, which is sometimes one second before midnight
        // and sometimes exactly midnight depending on whether a measure uses '<' or '<='. In either case we want to
        // report the full day prior to that end time.
        var oneDayMinusOneSec = 86399000;
        if (dueEndDate !== null && dueStartDate !== null) {
            oneDayMinusOneSec = Math.min(oneDayMinusOneSec, (dueEndDate - dueStartDate) / 2);
        }
        var dueStartDateStr = dueStartDate !== null ? el8.service.tz.toTzDateTime(dueStartDate + oneDayMinusOneSec).asString() : null;
        var dueEndDateStr = dueEndDate !== null ? el8.service.tz.toTzDateTime(dueEndDate - oneDayMinusOneSec).asString() : null;

        return {
            dueStartDate: dueStartDate,
            dueStartDateStr: dueStartDateStr,
            dueEndDate: dueEndDate,
            dueEndDateStr: dueEndDateStr
        };
    };

    that.findAndCalculateDueDates = function(timelapseMap, labels, queryDate, noDeadlineLookahead) {
        var recordsAndDeadlines = that.findRecordsAndDeadlines(timelapseMap, labels, queryDate,
                                                               noDeadlineLookahead);

        var result = {};
        _.each(recordsAndDeadlines, function(recordAndDeadline, label) {
            result[label] = [
                recordAndDeadline[0],
                that.calculateDueDates(recordAndDeadline[1])
            ];
        });

        return result;
    };

    that.classifyDueDates = function(dueDates, now) {
        if (dueDates === null) {
            return 'impossible';
        }

        var dueStartDate = dueDates.dueStartDate;
        var dueEndDate = dueDates.dueEndDate;

        if (dueEndDate !== null && dueEndDate <= now) {
            return 'overdue';
        }
        else if (dueStartDate !== null && dueStartDate > now && dueEndDate !== null) {
            return 'interval';
        }
        else if (dueEndDate !== null) { // No dueStartDate or dueStartDate <= now
            return 'ending';
        }
        else if (dueStartDate !== null) { // No dueEndDate
            return dueStartDate > now ? 'starting' : 'since';
        }
        else {
            return 'none';
        }
    };

    that.parseLabelsFromGuidance = function(measureGuidance) {
        var populationLabelInfo = {};

        _.each(measureGuidance, function (populationGuidance) {
            if (!populationGuidance.elements) { return; }
            var labelInfo = [];

            _.each(populationGuidance.elements, function (element) {
                if (_.isString(element)) {
                    labelInfo.push(that.parseLabelsAndConcat(element));
                }
                else {
                    labelInfo.push(that.parseLabelsAndConcat(element.summary));
                    if (element.chosen_items_list) {
                        _.each(element.chosen_items_list, function(itemDesc) {
                            labelInfo.push(that.parseLabelsAndConcat(itemDesc));
                        });
                    }
                }
            });

            populationLabelInfo[populationGuidance.label] = labelInfo;
        });

        return populationLabelInfo;
    };

    that.parseLabels = function(summaryText) {
        var parts = summaryText.split(/((?:~?\$\w+)+)(?:\[([^\]]*)\])?/);
        var result = [];

        for (var i = 0; i < parts.length; i += 3) {
            result.push(parts[i]);
            if (i === parts.length - 1) { break; }

            var labels = parts[i + 1].split(/(?=\$)/g);
            for (var j = 0; j < labels.length; j++) {
                if (labels[j].endswith('~')) {
                    // TODO: find a regex that will split this properly in a single pass?
                    labels[j] = labels[j].slice(0, -1);
                    labels[j + 1] = '~' + labels[j + 1];
                }
            }
            if (labels[0] === "") { labels.shift(); }

            var replacementText = parts[i + 2] ||
                _.map(labels, function(label) {
                    return label.replace(/^~?\$/, '').replace(/_/g, ' ');
                }).join(', ');

            result.push({
                labels: labels,
                replacementText: replacementText
            });
        }

        return result;
    };

    that.parseLabelsAndConcat = function(summaryText) {
        var parts = that.parseLabels(summaryText);
        var cleanedSummary = '';
        var allLabels = [];
        _.each(parts, function(part) {
            if (part.labels) {
                _.each(part.labels, function(label) {
                    allLabels.push(label);
                });
                cleanedSummary += part.replacementText;
            }
            else {
                cleanedSummary += part;
            }
        });
        cleanedSummary = cleanedSummary.charAt(0).toUpperCase() + cleanedSummary.substr(1);
        return {
            labels: allLabels,
            sourceText: summaryText,
            cleanedText: cleanedSummary
        };
    };

    that.getReminderSummaryPrimaryLabels = function(reminderSpec) {
        var textParts = that.parseLabels(reminderSpec.summary);
        var primaryItemName = textParts[0];
        var primaryLabels = null;
        if (textParts.length > 1) {
            primaryItemName = textParts[1].replacementText;
            primaryLabels = textParts[1].labels;
        }

        return {
            name: primaryItemName,
            labels: primaryLabels
        };
    };

    that.collectReminderLabels = function(reminderSpec) {
        return that.collectPostprocessLabels(reminderSpec.trigger);
    };

    that.collectPostprocessLabels = function(postprocessSpec) {
        var allLabels = {};
        _.each(postprocessSpec, function(logicExpression, targetLabel) {
            var labels = [];
            _.each(logicExpression, function(conjunctionStmt) {
                if (conjunctionStmt.all) { Array.prototype.push.apply(labels, conjunctionStmt.all); }
                if (conjunctionStmt.none) { Array.prototype.push.apply(labels, conjunctionStmt.none); }
            });
            allLabels[targetLabel] = labels;
        });
        return allLabels;
    };

    that.extractGuidanceLabelsForReminder = function(reminder, labelsFromGuidance) {
        var extractedLabels = {};
        var reminderLabelsMap = el8.shared.util.cqm.collectReminderLabels(reminder);

        // Get the population labels ($numerator, $denominator, etc) used for each part of the
        // reminder trigger ($relevant, $excluded, $pending)
        _.each(reminderLabelsMap, function(populationLabels, reminderStateLabel) {
            // Cross-reference each population label with the measure guidance
            _.each(populationLabels, function(populationLabel) {
                // Get all the measure guidance elements for each population label
                _.each(labelsFromGuidance[populationLabel], function(labelInfo) {
                    _.each(labelInfo.labels, function(label) {
                        extractedLabels[label] = {
                            labelInfo: labelInfo,
                            reminderStateLabel: reminderStateLabel
                        };
                    });
                });
            });
        });

        return extractedLabels;
    };

    that.getReminderItemDueInfo = function(reminder, timelapseMap, queryDate) {
        var primaryItem = that.getReminderSummaryPrimaryLabels(reminder);

        var dueInfo = null;
        if (primaryItem.labels) {
            // For reminders, we want noDeadlineLookahead because the reminder
            // should be relevant and actionable within the current measurement
            // period. See CMS147 for an example of how to have a reminder for
            // actions that need to be taken in advance of the upcoming
            // measurement period.
            dueInfo = that.findAndCalculateDueDates(timelapseMap, primaryItem.labels, queryDate, true);
        }

        return {
            itemName: primaryItem.name,
            dueInfo: dueInfo
        };
    };

    return that;
})();
